import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Lock, LogOut, Phone, Mail, MapPin, Calendar,
  Image, Clock, MessageCircle, FileText, Zap,
  ArrowLeft, FileDown, Send, Users, CreditCard,
  BarChart3, Radio, MessageSquare, CalendarCheck
} from "lucide-react";

import { statusColors } from "@/data/adminData";
import { QuoteModal } from "@/components/admin/QuoteModal";
import { FollowupTab } from "@/components/admin/FollowupTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { CampaignTab } from "@/components/admin/CampaignTab";
import { LeadDetails } from "@/components/admin/LeadDetails";
import { PushNotificationToggle } from "@/components/admin/PushNotificationToggle";
import { RealtimeTab } from "@/components/admin/RealtimeTab";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { AppointmentsTab } from "@/components/admin/AppointmentsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [simulations, setSimulations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteInitData, setQuoteInitData] = useState(null);
  const [autoQuoteEnabled, setAutoQuoteEnabled] = useState(false);
  const [togglingAutoQuote, setTogglingAutoQuote] = useState(false);
  const [autoFollowupEnabled, setAutoFollowupEnabled] = useState(false);
  const [togglingFollowup, setTogglingFollowup] = useState(false);
  const [followupStats, setFollowupStats] = useState(null);
  const [triggeringFollowup, setTriggeringFollowup] = useState(false);
  const [followupSteps, setFollowupSteps] = useState([
    { key: "relance_1", hours: 24, subject: "Votre simulation d'enseigne est prête - PUB PRO", enabled: true },
    { key: "relance_2", hours: 72, subject: "Dernière chance : -500€ sur votre enseigne - PUB PRO", enabled: true },
    { key: "relance_3", hours: 168, subject: "On ne vous oublie pas - Offre spéciale PUB PRO", enabled: true },
  ]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [activeTab, setActiveTab] = useState("leads");

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fetchSimulations = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/simulations`);
      const sorted = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setSimulations(sorted);
    } catch { toast.error("Erreur lors du chargement"); }
    finally { setIsLoading(false); }
  };

  const fetchAutoQuoteSetting = async () => {
    try { const res = await axios.get(`${API}/admin/settings/auto-quote`); setAutoQuoteEnabled(res.data.enabled); } catch {}
  };

  const fetchFollowupSettings = async () => {
    try {
      const [settingRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/settings/auto-followup`),
        axios.get(`${API}/admin/followup/stats`)
      ]);
      setAutoFollowupEnabled(settingRes.data.enabled);
      if (settingRes.data.steps) setFollowupSteps(settingRes.data.steps);
      setFollowupStats(statsRes.data);
    } catch {}
  };

  const fetchAnalytics = async () => {
    try { const res = await axios.get(`${API}/admin/analytics`); setAnalyticsData(res.data); } catch {}
  };

  const toggleAutoQuote = async () => {
    setTogglingAutoQuote(true);
    try {
      const newVal = !autoQuoteEnabled;
      await axios.post(`${API}/admin/settings/auto-quote`, { enabled: newVal });
      setAutoQuoteEnabled(newVal);
      toast.success(newVal ? "Devis automatique activé" : "Devis automatique désactivé");
    } catch { toast.error("Erreur"); }
    finally { setTogglingAutoQuote(false); }
  };

  const toggleFollowup = async () => {
    setTogglingFollowup(true);
    try {
      const newVal = !autoFollowupEnabled;
      await axios.post(`${API}/admin/settings/auto-followup`, { enabled: newVal });
      setAutoFollowupEnabled(newVal);
      toast.success(newVal ? "Relances automatiques activées" : "Relances désactivées");
    } catch { toast.error("Erreur"); }
    finally { setTogglingFollowup(false); }
  };

  const saveFollowupConfig = async () => {
    setSavingConfig(true);
    try { await axios.post(`${API}/admin/settings/followup-config`, { steps: followupSteps }); toast.success("Configuration des relances sauvegardée"); }
    catch { toast.error("Erreur lors de la sauvegarde"); }
    finally { setSavingConfig(false); }
  };

  const updateStep = (index, field, value) => {
    setFollowupSteps(prev => { const updated = [...prev]; updated[index] = { ...updated[index], [field]: value }; return updated; });
  };

  const triggerFollowupNow = async () => {
    setTriggeringFollowup(true);
    try {
      const res = await axios.post(`${API}/admin/followup/trigger`);
      const count = res.data.emails_sent;
      toast.success(count > 0 ? `${count} relance(s) envoyée(s)` : "Aucune relance à envoyer");
      fetchFollowupSettings();
      fetchSimulations();
    } catch { toast.error("Erreur lors de l'envoi"); }
    finally { setTriggeringFollowup(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/simulations/${id}/status`, { status });
      toast.success(`Statut mis à jour: ${statusColors[status].label}`);
      fetchSimulations();
    } catch { toast.error("Erreur lors de la mise à jour"); }
  };

  const deleteSimulation = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) return;
    try {
      await axios.delete(`${API}/simulations/${id}`);
      toast.success("Demande supprimée");
      setSelectedSimulation(null);
      fetchSimulations();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      toast.success("Connexion réussie");
      fetchSimulations();
      fetchAutoQuoteSetting();
      fetchFollowupSettings();
    } catch { toast.error("Mot de passe incorrect"); }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth) {
      setIsAuthenticated(true);
      fetchSimulations();
      fetchAutoQuoteSetting();
      fetchFollowupSettings();
    }
  }, []);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <Toaster richColors position="top-center" />
        <div className="bg-[#121212] border border-[#262626] rounded-3xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-[#EFFF00]/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-[#EFFF00]" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Outfit',sans-serif]">Administration</h1>
            <p className="text-[#A3A3A3] mt-2">Accès réservé</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-[#1a1a1a] border-[#262626] text-white h-12" data-testid="admin-password" />
            <Button type="submit" className="w-full bg-[#EFFF00] text-black hover:bg-[#d4e000] h-12 font-bold" data-testid="admin-login-btn">
              Connexion
            </Button>
          </form>
          <a href="/" className="block text-center mt-6 text-[#666] hover:text-[#A3A3A3] text-sm">
            <ArrowLeft className="w-4 h-4 inline mr-1" /> Retour au site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster richColors position="top-center" />
      
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#262626] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold font-['Outfit',sans-serif]">Dashboard Admin</h1>
          <div className="flex items-center gap-4">
            <span className="text-[#A3A3A3] text-sm">{simulations.length} demande{simulations.length > 1 ? 's' : ''}</span>
            <button onClick={toggleAutoQuote} disabled={togglingAutoQuote}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${autoQuoteEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-[#1a1a1a] text-[#666] border border-[#262626] hover:text-[#A3A3A3]'}`}
              data-testid="auto-quote-toggle" title={autoQuoteEnabled ? "L'IA génère et envoie automatiquement les devis" : "Les devis sont générés manuellement"}>
              <Zap className={`w-3.5 h-3.5 ${autoQuoteEnabled ? 'text-green-400' : ''}`} />
              Devis Auto {autoQuoteEnabled ? 'ON' : 'OFF'}
            </button>
            <button onClick={toggleFollowup} disabled={togglingFollowup}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${autoFollowupEnabled ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-[#1a1a1a] text-[#666] border border-[#262626] hover:text-[#A3A3A3]'}`}
              data-testid="auto-followup-toggle" title={autoFollowupEnabled ? "Relances automatiques à 24h et 72h" : "Relances désactivées"}>
              <Clock className={`w-3.5 h-3.5 ${autoFollowupEnabled ? 'text-blue-400' : ''}`} />
              Relances {autoFollowupEnabled ? 'ON' : 'OFF'}
            </button>
            <PushNotificationToggle />
            <Button onClick={handleLogout} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-[#262626]" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "leads", icon: Users, label: `Leads (${simulations.length})` },
            { id: "appointments", icon: CalendarCheck, label: "Rendez-vous" },
            { id: "realtime", icon: Radio, label: "Temps Réel" },
            { id: "campaign", icon: Send, label: "Emailing" },
            { id: "followup", icon: Clock, label: "Relances", onActivate: fetchFollowupSettings },
            { id: "analytics", icon: BarChart3, label: "Analytics", onActivate: fetchAnalytics },
            { id: "reviews", icon: MessageSquare, label: "Avis Google" },
          ].map(tab => (
            <button key={tab.id}
              onClick={() => { setActiveTab(tab.id); tab.onActivate?.(); }}
              className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeTab === tab.id ? "bg-[#EFFF00] text-black" : "bg-[#1a1a1a] text-[#A3A3A3] hover:text-white"}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 inline mr-2" />{tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "followup" && (
          <FollowupTab
            autoFollowupEnabled={autoFollowupEnabled}
            toggleFollowup={toggleFollowup}
            togglingFollowup={togglingFollowup}
            followupSteps={followupSteps}
            updateStep={updateStep}
            saveFollowupConfig={saveFollowupConfig}
            savingConfig={savingConfig}
            followupStats={followupStats}
            triggerFollowupNow={triggerFollowupNow}
            triggeringFollowup={triggeringFollowup}
          />
        )}

        {activeTab === "analytics" && <AnalyticsTab analyticsData={analyticsData} />}
        {activeTab === "campaign" && <CampaignTab formatDate={formatDate} />}
        {activeTab === "realtime" && <RealtimeTab />}
        {activeTab === "reviews" && <ReviewsTab />}
        {activeTab === "appointments" && <AppointmentsTab />}

        {/* Leads tab */}
        {activeTab === "leads" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-lg font-semibold mb-4">Demandes de simulation</h2>
              {isLoading ? (
                <div className="text-center py-12 text-[#A3A3A3]">Chargement...</div>
              ) : simulations.length === 0 ? (
                <div className="text-center py-12 text-[#A3A3A3]">Aucune demande pour le moment</div>
              ) : (
                simulations.map((sim) => (
                  <div key={sim.id}
                    onClick={() => setSelectedSimulation(sim)}
                    className={`bg-[#121212] border rounded-xl p-4 cursor-pointer transition-all hover:border-[#EFFF00]/50 ${selectedSimulation?.id === sim.id ? 'border-[#EFFF00]' : 'border-[#262626]'}`}
                    data-testid={`simulation-card-${sim.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-white">{sim.name}</h3>
                        <p className="text-sm text-[#A3A3A3] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{sim.city}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[sim.status]?.bg} ${statusColors[sim.status]?.text}`}>
                        {statusColors[sim.status]?.label || sim.status}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-[#666]">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(sim.created_at)}</span>
                      {sim.photo_url && <span className="flex items-center gap-1 text-[#EFFF00]"><Image className="w-3 h-3" />Photo</span>}
                      {sim.followups_sent && sim.followups_sent.length > 0 && (
                        <span className="flex items-center gap-1 text-blue-400"><Clock className="w-3 h-3" />{sim.followups_sent.length} relance{sim.followups_sent.length > 1 ? 's' : ''}</span>
                      )}
                      {sim.ai_suggestion && <span className="flex items-center gap-1 text-green-400"><Zap className="w-3 h-3" />IA</span>}
                      {sim.payment_status === "acompte_received" && <span className="flex items-center gap-1 text-blue-400"><CreditCard className="w-3 h-3" />Acompte</span>}
                      {sim.payment_status === "paid" && <span className="flex items-center gap-1 text-green-400"><CreditCard className="w-3 h-3" />Soldé</span>}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="lg:col-span-1">
              <LeadDetails
                simulation={selectedSimulation}
                onStatusUpdate={updateStatus}
                onDelete={deleteSimulation}
                onOpenQuoteModal={(data) => { setQuoteInitData(data); setShowQuoteModal(true); }}
                onRefresh={fetchSimulations}
              />
            </div>
          </div>
        )}

        {/* Quote Modal */}
        {showQuoteModal && selectedSimulation && (
          <QuoteModal
            simulation={selectedSimulation}
            onClose={() => setShowQuoteModal(false)}
            onSuccess={() => fetchSimulations()}
          />
        )}
      </div>
    </div>
  );
}

export default AdminPage;
